import { type ReactNode } from "react"

import {
  type ColumnChooserColumnMetadata,
  type ColumnChooserCopy,
} from "~/components/planning/columnChooser/ColumnChooserDialog"
import { getSharedGroupIcon } from "~/components/reusable/sharedColumnChooserMetadata"
import { Calculator } from "@phosphor-icons/react"
import { formatName } from "~/utils/namingUtils"
import { POColumnGroups, PurchaseOrderTableColumn } from "~/constants/purchaseOrderTable"
import { ProductTableColumn } from "~/constants/productTableColumns"
import { formatSecondaryMOQName } from "~/utils/formatting"

const PO_SPECIFIC_LABELS: Record<string, string> = {
  buffer: "Safety Stock",
  incomingStock: "Incoming stock (In period)",
  forecast: "Demand",
  preorders: `${formatName("preorder", true, true)}`,
  quantity: "Quantity",
  suggestedOrder: "Suggested Order",
  secondaryMoqGrouper: "Secondary MOQ Group",
  comments: "Comments",
  actions: "Actions",
}

function getPOColumnLabel(columnId: string): string | undefined {
  if (columnId === PurchaseOrderTableColumn.SecondaryMOQ) return formatSecondaryMOQName()
  return PO_SPECIFIC_LABELS[columnId]
}

export type ColumnChooserEntry = {
  id: string
  displayName?: string
  description?: string
  group: POColumnGroups
  keyWords?: string[]
}

function getPOTableColumns(): ColumnChooserColumnMetadata[] {
  return [
    {
      id: ProductTableColumn.Tags,
      description: "Tags associated with the product variant.",
      group: POColumnGroups.ProductInformation,
      keyWords: ["tags", "labels"],
    },
    {
      id: PurchaseOrderTableColumn.Comments,
      description: "Comments associated with the product variant.",
      group: POColumnGroups.ProductInformation,
      keyWords: ["comments", "notes", "remarks"],
    },
    {
      id: PurchaseOrderTableColumn.Quantity,
      description: "The quantity to order for this SKU.",
      group: POColumnGroups.OrderQuantities,
      keyWords: ["quantity", "order quantity", "amount"],
    },
    {
      id: ProductTableColumn.InventoryTurn,
      description:
        "Cost of goods sold over the last 12 months divided by average monthly inventory value.",
      group: POColumnGroups.AvailabilityAndInventory,
      keyWords: ["inventory turn", "stock turn", "turnover", "turn rate"],
    },
    {
      id: ProductTableColumn.GMROI,
      displayName: "GMROI",
      description: "Gross Margin Return on Investment.",
      group: POColumnGroups.AvailabilityAndInventory,
      keyWords: ["gross margin return on investment", "gmroi", "return on inventory investment"],
    },
    {
      id: ProductTableColumn.ABC,
      description:
        "Classification of products based on revenue (A = top 70%, B = next 20%, C = remaining 10%).",
      group: POColumnGroups.ModelsAndAnalysis,
      keyWords: ["abc", "abc analysis", "sales classification"],
    },
    {
      id: ProductTableColumn.WeekCover,
      description:
        "Safety stock expressed as weeks of cover (only shown for SKUs configured with week cover buffer type).",
      group: POColumnGroups.OperationalMetrics,
      keyWords: ["week cover", "weeks of cover", "safety stock weeks"],
    },
    {
      id: ProductTableColumn.MinimumOrderQuantity,
      group: POColumnGroups.MOQAndConstraints,
    },
    {
      id: PurchaseOrderTableColumn.Actions,
      description: "Actions available for this row.",
      group: POColumnGroups.Actions,
      keyWords: ["actions", "operations", "controls"],
    },
  ]
}

function getPOGroupIcon(groupName: string): ReactNode {
  if (groupName === POColumnGroups.Actions) {
    return <Calculator size={16} className="text-fg-muted" />
  }
  return getSharedGroupIcon(groupName)
}

/** The purchase order table's column chooser copy and grouping, plus the caller's own entries. */
export const poChooserCopy = (
  additionalEntries: readonly ColumnChooserEntry[],
): ColumnChooserCopy => ({
  columnMetadata: [...getPOTableColumns(), ...additionalEntries],
  getColumnLabel: getPOColumnLabel,
  getGroupIcon: getPOGroupIcon,
  otherGroupLabel: POColumnGroups.Other,
  trackingPrefix: "PO",
})
